import { Box, Typography, Link } from "@mui/material";
import BannerReportTIM from "./BannerReportTIM";
import appendix from "../data/timAppendix.json";
import { BANDS, moduleBand } from "../data/timAppendixMapping";
import { learningUrl } from "../data/learningLinks";

const Rec = ({ rec }) => (
  <Box sx={{ mb: 1.5 }}>
    {rec.heading && (
      <Typography component="span" sx={{ fontWeight: 700, color: "#0F5385" }}>
        {rec.heading}:{" "}
      </Typography>
    )}
    <Typography component="span" sx={{ color: "#33475b", lineHeight: 1.6 }}>
      {rec.body}
    </Typography>
  </Box>
);

// One audience block ("For the Team" / "For the Team Leader") as 3 colored
// columns. The team's actual band (if known) is emphasized; the others dim.
const BandColumns = ({ label, bands, highlightKey }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#0F5385", mb: 1 }}>
      {label}
    </Typography>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
        gap: 2,
        alignItems: "stretch",
      }}
    >
      {BANDS.map((b) => {
        const isHit = highlightKey === b.key;
        const dim = highlightKey && !isHit;
        return (
          <Box
            key={b.key}
            sx={{
              border: isHit ? `3px solid ${b.color}` : "1px solid #e0e6eb",
              borderRadius: 1,
              overflow: "hidden",
              backgroundColor: "#fff",
              opacity: dim ? 0.5 : 1,
              boxShadow: isHit ? `0 0 0 3px ${b.color}22` : "none",
            }}
          >
            <Box sx={{ backgroundColor: b.color, color: "#fff", px: 1.5, py: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box>
                <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>{b.label}</Typography>
                <Typography variant="caption" sx={{ color: "#fff", opacity: 0.95 }}>{b.range}</Typography>
              </Box>
              {isHit && (
                <Typography variant="caption" sx={{ color: "#fff", fontWeight: 700, border: "1px solid #fff", borderRadius: 1, px: 0.75, py: 0.25, whiteSpace: "nowrap" }}>
                  ★ YOUR TEAM
                </Typography>
              )}
            </Box>
            <Box sx={{ p: 1.5 }}>
              {(bands?.[b.key] || []).map((rec, i) => (
                <Rec key={i} rec={rec} />
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  </Box>
);

const LearningList = ({ items }) => (
  <Box sx={{ backgroundColor: "#f1f7fb", borderLeft: "4px solid #0F5385", p: 2, mb: 1 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#0F5385", mb: 1 }}>
      For All — Know2Grow Recommended Formal Learning Opportunities
    </Typography>
    <Box component="ul" sx={{ m: 0, pl: 3 }}>
      {(items || []).map((it, i) => {
        const url = learningUrl(it);
        return (
          <Typography component="li" key={i} sx={{ color: "#33475b", mb: 0.5 }}>
            {url ? (
              <Link href={url} target="_blank" rel="noopener noreferrer" sx={{ color: "#0F5385", fontWeight: 600 }}>
                {it}
              </Link>
            ) : (
              it
            )}
          </Typography>
        );
      })}
    </Box>
  </Box>
);

const TeamBandCallout = ({ band }) => {
  const def = BANDS.find((b) => b.key === band.bandKey);
  if (!def) return null;
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, backgroundColor: `${def.color}1A`, border: `1px solid ${def.color}`, borderRadius: 1, px: 1.5, py: 0.5, mb: 1.5 }}>
      <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: def.color }} />
      <Typography variant="body2" sx={{ color: "#33475b" }}>
        Your team: <strong style={{ color: def.color }}>{band.score}% — {def.label}</strong>
      </Typography>
    </Box>
  );
};

const AppendixTIM = ({ blocksOverallData }) => (
  <Box display="flex" flexDirection="column">
    <BannerReportTIM image={true} text={appendix.title} />
    <Box sx={{ padding: { xs: "12px 16px", sm: "14px 24px", md: "16px 48px" } }}>
      <Typography sx={{ color: "#5a6b7b", mb: 3 }}>
        For each module below, your team's current level is highlighted based on your assessment
        scores. Focus on the highlighted column, then explore the formal learning options.
      </Typography>
      {appendix.modules.map((m, i) => {
        const band = moduleBand(blocksOverallData, i);
        return (
          <Box key={i} sx={{ mb: 5 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#0F5385", mb: 1 }}>
              {m.module}
            </Typography>
            {band && <TeamBandCallout band={band} />}
            <Typography sx={{ color: "#33475b", mb: 2, lineHeight: 1.6 }}>{m.intro}</Typography>
            <BandColumns label="For the Team" bands={m.team} highlightKey={band?.bandKey} />
            <BandColumns label="For the Team Leader" bands={m.leader} highlightKey={band?.bandKey} />
            <LearningList items={m.forAll} />
          </Box>
        );
      })}
    </Box>
  </Box>
);

export default AppendixTIM;
